import {
  COGNITO_CALLBACK_URL,
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
  COGNITO_LOGOUT_URL,
} from '@config/config';

/**
 * Supported federated identity provider names as registered in Cognito.
 *
 * - "Google" matches the built-in Google provider name.
 * - "SignInWithLinkedIn" is the name given to the LinkedIn OIDC provider
 *   configured in the Cognito User Pool.
 *
 * Required IdP configuration (avoids Cognito /error 400):
 * - Google Cloud Console → Credentials → OAuth 2.0 Client → Authorized redirect URIs
 *   must include exactly: {COGNITO_DOMAIN}/oauth2/idpresponse
 *   e.g. https://dev-fibonaccipartner.auth.us-east-1.amazoncognito.com/oauth2/idpresponse
 */
export type FederatedProvider = 'Google' | 'Linkedin';

/** Cognito identity_provider query value for Google. */
export const COGNITO_IDP_GOOGLE = 'Google';

/** Cognito identity_provider query value for LinkedIn (name in User Pool). */
export const COGNITO_IDP_LINKEDIN = 'Linkedin';

const IDP_MAP: Record<FederatedProvider, string> = {
  Google: COGNITO_IDP_GOOGLE,
  Linkedin: COGNITO_IDP_LINKEDIN,
};

/**
 * Returns the Cognito identity_provider query value for a given provider.
 */
export const getCognitoIdentityProvider = (
  provider: FederatedProvider,
): string => IDP_MAP[provider];

/**
 * Build the Cognito /oauth2/authorize URL for a given identity provider.
 *
 * Passing `identity_provider` on the query string skips the Cognito Hosted UI
 * provider-selection page and sends the user straight to the social provider.
 * The `redirect_uri` points to the **backend** callback endpoint so that the
 * authorization code is exchanged server-side.
 *
 * @deprecated Prefer using react-oidc-context's signinRedirect() with
 * extraQueryParams.identity_provider for federated login.
 */
export const getFederatedLoginUrl = (provider: FederatedProvider): string => {
  const params = new URLSearchParams({
    identity_provider: getCognitoIdentityProvider(provider),
    response_type: 'code',
    client_id: COGNITO_CLIENT_ID,
    redirect_uri: COGNITO_CALLBACK_URL,
    scope: 'openid email profile aws.cognito.signin.user.admin',
  });
  return `${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
};

/**
 * Build the Cognito logout URL for federated sign-out.
 * Redirecting here logs the user out of Cognito and then sends them to logoutUri.
 *
 * @param logoutUri - URL to redirect to after logout (e.g. app login page). Defaults to COGNITO_LOGOUT_URL from env.
 */
export const getCognitoLogoutUrl = (logoutUri?: string): string => {
  const uri = logoutUri ?? COGNITO_LOGOUT_URL ?? `${window.location.origin}/auth/login`;
  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    logout_uri: uri,
  });
  return `${COGNITO_DOMAIN}/logout?${params.toString()}`;
};
